// Svelte 5 reactive timeline viewport state.
//
// The viewport describes WHERE on the (potentially long) time axis the user is
// currently looking. It is the single source of truth for both zoom (pxPerSec)
// and horizontal scroll (scrollX, in pixels). All track renderers, the
// waveform, the playhead, the ruler, and the minimap derive their geometry
// from this state, so changes here drive the whole Konva scene graph.

const ZOOM_MIN = 5;     // px/sec
const ZOOM_MAX = 1000;  // px/sec
const ZOOM_STEP = 1.5;  // multiplier per +/- press
const DEFAULT_PX_PER_SEC = 100;

export const viewportState = $state({
    pxPerSec: DEFAULT_PX_PER_SEC,
    scrollX: 0,
    containerWidth: 0,   // body width minus the left labels gutter, in px
    duration: 0,         // media duration in seconds (set when player loads)
    ready: false         // becomes true after the first measurement
});

/**
 * Convert a time in seconds to an x coordinate inside the scrolling content
 * Konva Group. (i.e. the x coordinate BEFORE we apply the negative-scroll
 * translation on the Group.)
 * @param {number} timeSec
 * @returns {number}
 */
export function timeToX(timeSec) {
    return timeSec * viewportState.pxPerSec;
}

/**
 * Convert a Stage x coordinate (i.e. visible-area pixel position) back to
 * time in seconds, accounting for the current scroll.
 * @param {number} x
 * @returns {number}
 */
export function xToTime(x) {
    if (!viewportState.pxPerSec) return 0;
    return (x + viewportState.scrollX) / viewportState.pxPerSec;
}

/**
 * Currently visible time window as `[startSec, endSec]`, optionally padded
 * by a buffer expressed in viewport widths (default 1 = pad one viewport
 * worth of time on each side for off-screen culling).
 * @param {number} [bufferRatio]
 * @returns {[number, number]}
 */
export function visibleTimeRange(bufferRatio = 1) {
    const { scrollX, containerWidth, pxPerSec } = viewportState;
    if (!pxPerSec || !containerWidth) return [0, 0];
    const pad = containerWidth * bufferRatio;
    const startPx = scrollX - pad;
    const endPx = scrollX + containerWidth + pad;
    return [Math.max(0, startPx / pxPerSec), endPx / pxPerSec];
}

/**
 * Clamp a candidate scrollX to the legal range based on duration and current
 * zoom. Allows scrollX = 0 even when duration is unknown.
 * @param {number} candidate
 * @returns {number}
 */
function clampScrollX(candidate) {
    const { duration, pxPerSec, containerWidth } = viewportState;
    if (candidate < 0) return 0;
    if (!duration || !pxPerSec) return candidate;
    const contentWidth = duration * pxPerSec;
    const maxScroll = Math.max(0, contentWidth - containerWidth);
    return Math.min(candidate, maxScroll);
}

/**
 * Set the scrollX position, clamped to legal range.
 * @param {number} value
 */
export function setScrollX(value) {
    viewportState.scrollX = clampScrollX(value);
}

/**
 * Scroll by a delta in pixels.
 * @param {number} dx
 */
export function panBy(dx) {
    setScrollX(viewportState.scrollX + dx);
}

/**
 * Set zoom to a specific pxPerSec value, anchoring around a stage x coordinate
 * if provided (so the time under the cursor stays put while zooming).
 *
 * @param {number} pxPerSec - target zoom
 * @param {number} [anchorX] - stage x coordinate to anchor on (visible space)
 */
export function setZoom(pxPerSec, anchorX) {
    const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, pxPerSec));
    if (next === viewportState.pxPerSec) return;
    const prev = viewportState.pxPerSec;

    if (typeof anchorX === "number" && prev > 0) {
        // Keep the time at anchorX fixed: time = (x + scrollX) / pxPerSec
        const anchorTime = (anchorX + viewportState.scrollX) / prev;
        viewportState.pxPerSec = next;
        viewportState.scrollX = clampScrollX(anchorTime * next - anchorX);
    } else {
        viewportState.pxPerSec = next;
        viewportState.scrollX = clampScrollX(viewportState.scrollX);
    }
}

/**
 * Zoom in one step (anchored on a stage x if provided).
 * @param {number} [anchorX]
 */
export function zoomIn(anchorX) {
    setZoom(viewportState.pxPerSec * ZOOM_STEP, anchorX);
}

/**
 * Zoom out one step (anchored on a stage x if provided).
 * @param {number} [anchorX]
 */
export function zoomOut(anchorX) {
    setZoom(viewportState.pxPerSec / ZOOM_STEP, anchorX);
}

/**
 * Center the viewport on a given time in seconds.
 * @param {number} timeSec
 */
export function centerOnTime(timeSec) {
    const targetScroll = timeSec * viewportState.pxPerSec - viewportState.containerWidth / 2;
    setScrollX(targetScroll);
}

/**
 * Scroll so a time is visible, optionally centered.
 * @param {number} timeSec
 * @param {{ center?: boolean }} [opts]
 */
export function scrollToTime(timeSec, opts = {}) {
    if (opts.center) {
        centerOnTime(timeSec);
        return;
    }
    const x = timeSec * viewportState.pxPerSec;
    const { scrollX, containerWidth } = viewportState;
    if (x < scrollX) {
        setScrollX(x);
    } else if (x > scrollX + containerWidth) {
        setScrollX(x - containerWidth);
    }
}

/**
 * Set or update the container width (called when the timeline resizes).
 * @param {number} width
 */
export function setContainerWidth(width) {
    viewportState.containerWidth = Math.max(0, Math.floor(width || 0));
    viewportState.scrollX = clampScrollX(viewportState.scrollX);
    viewportState.ready = viewportState.containerWidth > 0;
}

/**
 * Set the known media duration (lets us clamp scroll).
 * @param {number} durationSec
 */
export function setDuration(durationSec) {
    viewportState.duration = Number.isFinite(durationSec) && durationSec > 0 ? durationSec : 0;
    viewportState.scrollX = clampScrollX(viewportState.scrollX);
}

/**
 * Reset the viewport to defaults (used when a project is closed).
 */
export function resetViewport() {
    viewportState.pxPerSec = DEFAULT_PX_PER_SEC;
    viewportState.scrollX = 0;
    viewportState.containerWidth = 0;
    viewportState.duration = 0;
    viewportState.ready = false;
}

export const ZOOM_LIMITS = { min: ZOOM_MIN, max: ZOOM_MAX, step: ZOOM_STEP };
